import { BUTTON_SIZE, BUTTON_TYPE, DSButton, DSModal, DSTooltip } from '@repo/design-system/index'
import { QIcon_alert, QIcon_chevron_right, QIcon_information_outline, QIcon_tutor } from '@repo/icons/index'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import 'react-reflex/styles.css'
import Cookies from 'universal-cookie'
import apis from '../../../../modules/apis'
import { axiosGet } from '../../../../modules/axiosHelper'
import useAppRouter from '../../../../modules/hooks/common/useAppRouter'
import { n_common, n_review, s_classroom, s_evaluation } from '../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../modules/i18n/useTranslation'
import urls from '../../../../modules/urls'
import { COLORS, COOKIE_OPT, ZOOM_HELP_TOOLTIP } from '../../../../modules/vars'
import EvaluationModalBody from '../../../new-portal/lessons/evaluation/EvaluationModalBody'
import LessonEvaluationReport from '../lessons/LessonEvaluationReport'

export interface IShowPopup {
  exitModal: boolean
  evaluationModal: boolean
  reportModal: boolean
  reportConfirm: boolean
}

interface IClassroomPopupBody {
  showPopup: IShowPopup
  setShowPopup: any
  handleModalRightButton: any
  activateDocs: any
  isClassroom?: boolean
}

export const ClassroomPopupBody = ({
  showPopup,
  setShowPopup,
  handleModalRightButton,
  activateDocs,
  isClassroom = false,
}: IClassroomPopupBody) => {
  const { t } = useTranslation()
  const { query } = useRouter()
  const router = useAppRouter()
  const { data, isFetching } = useQuery({
    queryKey: [query.lessonId, 'Evaluation'],
    queryFn: () => fetchData(),
    enabled: showPopup.evaluationModal || showPopup.reportModal,
  })
  const queryClient = useQueryClient()

  const fetchData = async () => {
    const response = await axiosGet(apis.lesson.get(query.lessonId))

    return response
  }

  return (
    <div className="">
      {showPopup.exitModal && (
        <DSModal
          id="exitModal"
          size="lg"
          isOpen={showPopup.exitModal}
          setIsOpen={() => setShowPopup({ ...showPopup, exitModal: (prev) => !prev })}
        >
          <DSModal.Title>{t(n_review.modal_exit.title)}</DSModal.Title>
          <DSModal.Content>
            <div className="">{t(n_review.modal_exit.desc)}</div>
            <div
              className="mt-[32px] flex cursor-pointer flex-row justify-between rounded-[4px] bg-gray-50 p-[8px]"
              onClick={(e) => {
                e.preventDefault()
                setShowPopup({ ...showPopup, reportModal: true })
              }}
            >
              <div className="flex flex-row items-center gap-[4px]">
                <QIcon_alert width={20} height={20} color={COLORS.GRAY400} />
                <div className="Body2-14Md">{t(n_review.step_1.report)}</div>
              </div>
              <QIcon_chevron_right width={20} height={20} color={COLORS.GRAY500} />
            </div>
          </DSModal.Content>
          <DSModal.ButtonContainer direction="horizontal">
            <DSButton
              size={BUTTON_SIZE.MD}
              type={BUTTON_TYPE.OUTLINE}
              customBgColor={COLORS.WHITE}
              onClick={(e) => {
                e.preventDefault()
                setShowPopup({ ...showPopup, exitModal: false })
              }}
            >
              <DSButton.Label>{t(n_common.btn_cancel)}</DSButton.Label>
            </DSButton>
            <DSButton
              size={BUTTON_SIZE.MD}
              onClick={() => {
                handleModalRightButton()
                setShowPopup({ ...showPopup, exitModal: false })
              }}
            >
              <DSButton.Label>{t(n_common.btn_leave)}</DSButton.Label>
            </DSButton>
          </DSModal.ButtonContainer>
        </DSModal>
      )}
      {showPopup.reportModal && (
        <DSModal
          id="reportModal"
          size="xl"
          isOpen={showPopup.reportModal}
          setIsOpen={() => setShowPopup({ ...showPopup, reportModal: (prev) => !prev })}
        >
          <DSModal.Content className="min-h-[600px]">
            {data && !isFetching && (
              <LessonEvaluationReport
                lessonId={Number(query.lessonId)}
                lessonData={data.lesson}
                onClose={() => {
                  setShowPopup({ ...showPopup, reportModal: false })
                  router.push(urls.student.newPortal.lessons.reviews.home)
                }}
              />
            )}
          </DSModal.Content>
        </DSModal>
      )}
      {showPopup.evaluationModal && (
        <DSModal
          closeButton={true}
          id="evaluationModal"
          isOpen={showPopup.evaluationModal}
          setIsOpen={() => setShowPopup({ ...showPopup, evaluationModal: (prev) => !prev })}
          size="custom"
          className="h-full max-h-[661px] w-full max-w-[660px] rounded-[12px] bg-white p-[40px]"
        >
          <DSModal.Content className="h-full">
            {data && !isFetching && (
              <EvaluationModalBody
                lessonId={Number(query.lessonId)}
                lessonData={data.lesson}
                onClose={() => {
                  activateDocs()
                  setShowPopup({ ...showPopup, evaluationModal: false })
                }}
                showReportModal={() => {
                  setShowPopup({ ...showPopup, reportModal: false })
                }}
              />
            )}
          </DSModal.Content>
        </DSModal>
      )}
      {showPopup.reportConfirm && (
        <DSModal
          closeButton={true}
          id="evaluationModal"
          isOpen={showPopup.evaluationModal}
          setIsOpen={() => setShowPopup({ ...showPopup, evaluationModal: (prev) => !prev })}
          size="custom"
          className="h-full max-h-[661px] w-full max-w-[660px] rounded-[12px] bg-white p-[40px]"
        >
          <DSModal.Content className="h-full">
            {data && !isFetching && (
              <EvaluationModalBody
                lessonId={Number(query.lessonId)}
                lessonData={data.lesson}
                onClose={() => {
                  activateDocs()
                  setShowPopup({ ...showPopup, evaluationModal: false })
                }}
              />
            )}
          </DSModal.Content>
        </DSModal>
      )}
    </div>
  )
}

type HelpBtnPropsType = {
  showChatBox: any
  setShowChatBox: any
}

export const HelpBtn = (props: HelpBtnPropsType) => {
  const { showChatBox, setShowChatBox } = props
  const { t } = useTranslation()

  return (
    <DSButton
      size={BUTTON_SIZE.SM}
      type={BUTTON_TYPE.OUTLINE}
      onClick={(e) => setShowChatBox(!showChatBox)}
      customBorderColor={COLORS.RED300}
      isIconMode={true}
      className="h-[42px]"
    >
      <DSButton.Icon alt="Help" className="mr-[4px]">
        <QIcon_information_outline width={16} height={16} color={COLORS.RED400} />
      </DSButton.Icon>
      <DSButton.Label customTxtColor={COLORS.RED400}>{t(s_evaluation.help)}</DSButton.Label>
    </DSButton>
  )
}

type ZoomGuideBtnPropsType = {
  setShowZoomGuide: any
  showZoomGuide: any
}

export const ZoomGuideBtn = (props: ZoomGuideBtnPropsType) => {
  const { setShowZoomGuide, showZoomGuide } = props
  const cookies = new Cookies()
  const [showTooltip, setShowTooltip] = useState<boolean>(true)
  const { t } = useTranslation()

  useEffect(() => {
    setShowTooltip(!cookies.get(ZOOM_HELP_TOOLTIP))
  }, [cookies?.get(ZOOM_HELP_TOOLTIP)])

  const hideTooltip = () => {
    cookies.set(ZOOM_HELP_TOOLTIP, true, COOKIE_OPT)
    setShowTooltip(false)
  }

  return (
    <>
      <DSTooltip
        place={'bottom'}
        hasCloseButton
        isMultiline={false}
        triggerComponent={
          <DSButton
            onClick={(e) => setShowZoomGuide(!showZoomGuide)}
            size={BUTTON_SIZE.SM}
            type={BUTTON_TYPE.OUTLINE}
            customBorderColor={COLORS.RED300}
            isIconMode={true}
            className="h-[42px]"
          >
            <DSButton.Icon alt="Zoom" className="mr-[4px]">
              <QIcon_tutor color={COLORS.RED300} width={16} height={16} />
            </DSButton.Icon>
            <DSButton.Label customTxtColor={COLORS.RED400}>{t(s_classroom.zoom_help)}</DSButton.Label>
          </DSButton>
        }
        contentComponent={t(s_classroom.zoom_tooltip)}
      />

      {/* <TooltipNew
        id="tooltip-zoom-help"
        place="bottom"
        clickable
        tooltipBgClx="bg-[#4d525d]"
        tooltipClx="text-h6 break-words"
        isOpen={showTooltip}
      >
        <div className="flex items-center justify-between gap-[14px]">
          <div className="whitespace-pre">{t(s_classroom.zoom_tooltip)}</div>
          <div className="cursor-pointer" onClick={hideTooltip}>
            <FeClose size={18} />
          </div>
        </div>
      </TooltipNew> */}
    </>
  )
}

export const MockSessionToggle = ({ tutor }) => {
  const [open, setOpen] = useState(true)
  return (
    <div className="absolute bg-white" style={{ left: 150, bottom: 0 }}>
      {open && (
        <div className="border-1 flex h-[500px] flex-row overflow-y-scroll rounded-[4px] border-gray-300 px-[8px] py-[16px] text-[14px]">
          <div className="w-[500px]">
            <div> 튜터의 목세션 사전 준비도</div>
            <div>- 평가 기준 읽기 {tutor?.read_evaluation ? 'o' : 'x'}</div>
            <div>- 목세션 비디오 {tutor?.watch_sample_mock ? 'o' : 'x'}</div>
            <div>- 목세션 퀴즈 {tutor?.mock_quiz ? 'o' : 'x'}</div>
            <div>- 링글닥 튜토리얼 {tutor?.ringledoc_tutorial ? 'o' : 'x'}</div>
            <div className="mt-[16px]">아래 세가지를 하지 않으면 바로 피드백 주세요!</div>
            <div className="mt-[8px] font-bold">1. 학생 답변을 받아쓰지 않는 경우</div>
            <div>"Could you write down my response as much as possible?"</div>
            <div className="mt-[2px] text-[12px]">(주의: 타이핑을 못한다고 너무 느리게 읽어주지는 말아주세요)</div>
            <div className="font-bodl mt-[8px]">2. 원문과 교정문이 너무 비슷한 경우</div>
            <div>
              "Could you paraphrase my sentences using your own words? Please suggest different vocabulary and sentence
              structures."
            </div>
            <div className="mt-[8px] font-bold">3. 교정에 대한 설명이 없거나 미흡한 경우</div>
            <div>"Could you explain your logic and reason behind your paraphrases?"</div>
            <div className="mt-[4px] text-red-500">
              마지막에 학생이 요청하지 않더라도 이 세 가지는 링글 튜터로서의 책임이라고 리마인더 해주시길 부탁드립니다.
            </div>
            <div
              className="text-underline text-secondary mt-[4px] cursor-pointer"
              onClick={() =>
                window.open(
                  'https://www.notion.so/ringle/Mock-Session-Interviewer-Guide-V-4-as-of-09-22-3dc625bd3ff64da3a19eb0d3a0546144',
                  '_blank'
                )
              }
            >
              목세션 가이드 참고하기
            </div>
            <div
              className="text-underline text-secondary mt-[4px] cursor-pointer"
              onClick={() =>
                window.open('https://admin.ringlefront.com/v2/admin/tutor/application/mock_evaluation/', '_blank')
              }
            >
              목세션 평가 어드민 (서포터즈는 별도 링크 이용 바랍니다.)
            </div>
          </div>
        </div>
      )}
      {!open && <div className="border-1 h-[30px] w-[30px] rounded-[4px] border-gray-300 text-[14px]"></div>}
      <div className="absolute cursor-pointer text-[14px]" style={{ right: 0, top: 0 }}>
        {open ? <div onClick={() => setOpen(!open)}>최소화</div> : <div onClick={() => setOpen(!open)}>최대화</div>}
      </div>
    </div>
  )
}
